import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  Typography,
  Card,
  Row,
  Col,
  Select,
  DatePicker,
  Input,
  Statistic,
  message,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getAuditTrail, getAuditEntityTypes, listUsers } from '../../api';
import { DateCell, DocCell, PersonCell, StatusPill } from '../../components/table-cells';
import type { Tone } from '../../components/table-cells';
import ResponsiveTable from '../../components/responsive/ResponsiveTable';

const { Title } = Typography;
const { RangePicker } = DatePicker;

/** Same colour vocabulary as the purchasing lists: green done, red stopped, blue in progress. */
const ACTION_TONES: Record<string, Tone> = {
  CREATE: 'ok',
  DELETE: 'bad',
  UPDATE: 'info',
  APPROVE: 'ok',
  REJECT: 'bad',
  STATUS_CHANGE: 'warn',
  SUBMIT: 'info',
  RECEIVE: 'info',
  RETURN: 'warn',
};

const ACTION_OPTIONS = [
  'CREATE', 'UPDATE', 'DELETE', 'STATUS_CHANGE',
  'APPROVE', 'REJECT', 'SUBMIT', 'RECEIVE', 'RETURN',
];

function getEntityLink(entityType: string, entityId: string): string | null {
  // Only link if entityId looks like a proper string ID (MR-xxx, PO-xxx, GRN-xxx)
  // Pure numeric IDs (like old MRLine records) can't be resolved to a page
  const isNumericOnly = /^\d+$/.test(entityId);

  if (entityType === 'MR' || entityType === 'MRHeader') {
    return `/mrs/${entityId}`;
  }
  if (entityType === 'MRLine') {
    // Old records have numeric line ID — can't link. New records have MR's mrId.
    return isNumericOnly ? null : `/mrs/${entityId}`;
  }
  if (entityType === 'PO' || entityType === 'POHeader') {
    return `/pos/${entityId}`;
  }
  if (entityType === 'POLine') {
    return isNumericOnly ? null : `/pos/${entityId}`;
  }
  if (entityType === 'GRN' || entityType === 'GRNHeader' || entityType === 'GRNReturn') {
    return isNumericOnly ? null : `/grns/${entityId}`;
  }
  if (entityType === 'CatalogItem') {
    return '/catalog';
  }
  return null;
}

const AuditTrail: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Filter state
  const [entityTypes, setEntityTypes] = useState<string[]>([]);
  const [users, setUsers] = useState<Array<{ id: number; displayName: string }>>([]);
  const [filterEntityTypes, setFilterEntityTypes] = useState<string[]>([]);
  const [filterUserIds, setFilterUserIds] = useState<number[]>([]);
  const [filterActions, setFilterActions] = useState<string[]>([]);
  const [filterEntityId, setFilterEntityId] = useState<string>('');
  const [filterDates, setFilterDates] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);

  // Stats
  const [todayCount, setTodayCount] = useState(0);
  const [uniqueUsers, setUniqueUsers] = useState(0);

  const fetchData = useCallback(async (p?: number) => {
    setLoading(true);
    try {
      const currentPage = p || page;
      const params: Record<string, any> = { page: currentPage, pageSize };
      if (filterEntityTypes.length > 0) params.entityTypes = filterEntityTypes.join(',');
      if (filterUserIds.length > 0) params.userIds = filterUserIds.join(',');
      if (filterActions.length > 0) params.actions = filterActions.join(',');
      if (filterEntityId.trim()) params.entityId = filterEntityId.trim();
      if (filterDates && filterDates[0]) params.dateFrom = filterDates[0].format('YYYY-MM-DD');
      if (filterDates && filterDates[1]) params.dateTo = filterDates[1].format('YYYY-MM-DD');

      const result = await getAuditTrail(params);
      setData(result.data);
      setTotal(result.total);
      setPage(result.page);

      // Compute stats from current result set
      const today = dayjs().startOf('day');
      const todayItems = result.data.filter((r: any) => dayjs(r.timestamp).isAfter(today));
      setTodayCount(todayItems.length);
      const userIds = new Set(result.data.map((r: any) => r.userId).filter(Boolean));
      setUniqueUsers(userIds.size);
    } catch (err) {
      message.error('Failed to load audit trail');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filterEntityTypes, filterUserIds, filterActions, filterEntityId, filterDates]);

  useEffect(() => {
    fetchData(1);
  }, []);

  useEffect(() => {
    getAuditEntityTypes().then(setEntityTypes).catch(() => {});
    listUsers().then((u: any[]) => setUsers(u.map((x: any) => ({ id: x.id, displayName: x.displayName })))).catch(() => {});
  }, []);

  const handleSearch = () => {
    setPage(1);
    fetchData(1);
  };

  const handleReset = () => {
    setFilterEntityTypes([]);
    setFilterUserIds([]);
    setFilterActions([]);
    setFilterEntityId('');
    setFilterDates(null);
    setPage(1);
    setTimeout(() => fetchData(1), 0);
  };

  const handleExportCSV = () => {
    if (data.length === 0) {
      message.warning('No data to export');
      return;
    }
    const headers = ['Timestamp', 'User', 'Action', 'Entity Type', 'Entity ID', 'Field', 'Old Value', 'New Value'];
    const rows = data.map((r: any) => [
      dayjs(r.timestamp).format('YYYY-MM-DD HH:mm:ss'),
      r.user?.displayName || 'System',
      r.action || '',
      r.entityType,
      r.entityId,
      r.fieldName,
      r.oldValue || '',
      r.newValue || '',
    ]);
    const csv = [headers, ...rows].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-trail-${dayjs().format('YYYY-MM-DD')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 170,
      render: (val: string) => <DateCell value={val} withTime />,
    },
    {
      title: 'User',
      dataIndex: 'user',
      key: 'user',
      width: 140,
      render: (user: any) => <PersonCell name={user?.displayName || 'System'} />,
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 130,
      render: (action: string) => (
        <StatusPill tone={ACTION_TONES[action] ?? 'mute'} label={action || 'N/A'} />
      ),
    },
    {
      title: 'Entity Type',
      dataIndex: 'entityType',
      key: 'entityType',
      width: 130,
    },
    {
      title: 'Entity ID',
      dataIndex: 'entityId',
      key: 'entityId',
      width: 120,
      render: (entityId: string, record: any) => (
        <DocCell id={entityId} linkTo={getEntityLink(record.entityType, entityId) ?? undefined} compact />
      ),
    },
    {
      title: 'Field',
      dataIndex: 'fieldName',
      key: 'fieldName',
      width: 140,
    },
    {
      title: 'Old Value',
      dataIndex: 'oldValue',
      key: 'oldValue',
      width: 160,
      ellipsis: true,
      render: (val: string | null) => val || '-',
    },
    {
      title: 'New Value',
      dataIndex: 'newValue',
      key: 'newValue',
      width: 160,
      ellipsis: true,
      render: (val: string | null) => val || '-',
    },
  ];

  return (
    <div>
      <Title level={3}>Audit Trail</Title>

      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={8}>
          <Card size="small">
            <Statistic title="Total Changes" value={total} />
          </Card>
        </Col>
        <Col xs={8}>
          <Card size="small">
            <Statistic title="Unique Users (page)" value={uniqueUsers} />
          </Card>
        </Col>
        <Col xs={8}>
          <Card size="small">
            <Statistic title="Today's Changes (page)" value={todayCount} />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={12} md={4}>
            <Select
              mode="multiple"
              placeholder="Entity Type"
              allowClear
              style={{ width: '100%' }}
              value={filterEntityTypes}
              onChange={setFilterEntityTypes}
              options={entityTypes.map(t => ({ label: t, value: t }))}
              maxTagCount="responsive"
              optionFilterProp="label"
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              mode="multiple"
              placeholder="User"
              allowClear
              optionFilterProp="label"
              style={{ width: '100%' }}
              value={filterUserIds}
              onChange={setFilterUserIds}
              options={users.map(u => ({ label: u.displayName, value: u.id }))}
              maxTagCount="responsive"
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              mode="multiple"
              placeholder="Action"
              allowClear
              style={{ width: '100%' }}
              value={filterActions}
              onChange={setFilterActions}
              options={ACTION_OPTIONS.map(a => ({ label: a, value: a }))}
              maxTagCount="responsive"
              optionFilterProp="label"
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <RangePicker
              style={{ width: '100%' }}
              value={filterDates as any}
              onChange={(dates) => setFilterDates(dates as any)}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Input
              placeholder="Entity ID"
              value={filterEntityId}
              onChange={(e) => setFilterEntityId(e.target.value)}
              onPressEnter={handleSearch}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                Search
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                Reset
              </Button>
              <Button icon={<DownloadOutlined />} onClick={handleExportCSV}>
                CSV
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Table */}
      <ResponsiveTable
        card={{ header: ['action', 'timestamp'] }}
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        size="small"
        scroll={{ x: 1100 }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          showTotal: (t) => `Total ${t} records`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
            fetchData(p);
          },
        }}
      />
    </div>
  );
};

export default AuditTrail;
